import Column from '@amzn/meridian/column';
import { FileDetails } from '@amzn/meridian/file-input';
import { FileDetailsFile } from '@amzn/meridian/file-input/file-details';
import Row from '@amzn/meridian/row';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import React, { FC, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { PutClient } from '../clients';
import { useHrpsApi } from '../clients/useHrpsApi';
import { Heading, useTranslation } from '../components/blocks';
import { Text, Button, FileInput } from '../components/blocks/localization';
import { Tile, usePageMessaging } from '../components/composites';
import { RootState } from '../redux/types';
import { CodedError, StartedExtractionStatus } from '../types';
import { Logger } from '../utils/logger';
import { useSiteMapRouter } from '../utils/SiteMapRouter';
import { sanitizeFilenameISO88591 } from '../utils/textUtils';

// Error codes the two extraction operations model, and so the only ones we have a message for.
const EXPLAINED_ERROR_CODES = [
    'AccessDenied',
    'Dependency',
    'InvalidInput',
    'NotAuthorizedForReseller',
    'NotAuthorizedToCreate',
    'NotAuthorizedToUpload',
    'UnsupportedDocumentType',
];

const toDisplayableErrorCode = (errorCode?: string) =>
    errorCode && EXPLAINED_ERROR_CODES.includes(errorCode) ? errorCode : 'InternalError';

const MAX_FILE_SIZE_MB = 4.5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

interface AttachedFile {
    id: string;
    file: File;
}

const isSameDocument = (one: File, other: File) =>
    one.name === other.name && one.size === other.size && one.lastModified === other.lastModified;

const UploadPurchaseOrderPage: FC<UploadPurchaseOrderPageProps> = ({ resellerId }) => {
    const { t } = useTranslation('uploadPurchaseOrderPage');
    const { cancel } = useSiteMapRouter();
    const { showSuccess, showError } = usePageMessaging();

    const [isPageBusy, setIsPageBusy] = useState(false);
    const [progress, setProgress] = useState<{ id: string; percent: number }[]>([]);
    const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
    const [startedExtractions, setStartedExtractions] = useState<StartedExtractionStatus[]>([]);

    const {
        extractions: { requestDocumentUploadUrl, startDocumentExtraction },
    } = useHrpsApi();

    const setFileProgress = (id: string, percent: number) => {
        setProgress((current) => [...current.filter((entry) => entry.id !== id), { id, percent }]);
    };

    const getFileProgress = (id: string) => progress.find((entry) => entry.id === id)?.percent ?? 0;

    const addFiles = (newFiles: File[]) => {
        const oversizedFiles = newFiles.filter((file) => file.size >= MAX_FILE_SIZE_BYTES);
        if (oversizedFiles.length) {
            showError(
                t('uploadPurchaseOrder-filesTooLarge', {
                    fileNames: oversizedFiles.map((file) => file.name).join(', '),
                    maxSize: MAX_FILE_SIZE_MB,
                })
            );
        }

        setAttachedFiles((current) => [
            ...current,
            ...newFiles
                .filter((newFile) => newFile.size < MAX_FILE_SIZE_BYTES)
                .filter((newFile) => !current.some((attached) => isSameDocument(attached.file, newFile)))
                .map((file) => ({ id: uuidv4(), file })),
        ]);
    };

    const removeFile = (id: string) => {
        setAttachedFiles((current) => current.filter((attached) => attached.id !== id));
    };

    // Each document is submitted independently so one bad file cannot stop the rest of the batch.
    const uploadPurchaseOrders = async () => {
        if (!attachedFiles.length) return;

        setIsPageBusy(true);
        const results: StartedExtractionStatus[] = [];

        await attachedFiles.reduce(async (promise, { id, file }) => {
            await promise;
            try {
                setFileProgress(id, 0);
                const uploadResult = await requestDocumentUploadUrl({
                    resellerId,
                    uploadType: 'PURCHASE_ORDER_EXTRACTION_UPLOAD',
                    filename: sanitizeFilenameISO88591(file.name),
                    mimeType: file.type,
                });
                await PutClient.putWithBucketDefaultEncryption(uploadResult.url, file);
                setFileProgress(id, 50);

                // StartExtraction is a handoff: HADES persists a tracking row and enqueues, then reads the
                // document in its async consumer (ExtractionProcessor -> getDocumentInformationWithRetry),
                // so unlike claims/returns, there is nothing to wait for between the upload and this call.
                const extraction = await startDocumentExtraction({
                    resellerId,
                    documentId: uploadResult.id,
                    documentType: 'PURCHASE_ORDER',
                });
                results.push({ id, fileName: file.name, extractionId: extraction.extractionId });
                setFileProgress(id, 100);
            } catch (error) {
                Logger.debug(`failed to start extraction for ${file.name} with error: ${error}`);
                results.push({
                    id,
                    fileName: file.name,
                    errorCode: toDisplayableErrorCode((error as CodedError)?.code),
                });
            }
        }, Promise.resolve());

        setStartedExtractions(results);
        if (results.some((result) => result.extractionId)) {
            showSuccess(t('uploadPurchaseOrder-successMessage'));
        }
        if (results.some((result) => result.errorCode)) {
            showError(t('uploadPurchaseOrder-errorMessage'));
        }
        setProgress([]);
        setAttachedFiles([]);
        setIsPageBusy(false);
    };

    return (
        <Column>
            <Row>
                <Heading level={4}>{t('uploadPurchaseOrder-pageTitle')}</Heading>
            </Row>
            <Row>
                <Text>{t('uploadPurchaseOrder-instruction')}</Text>
            </Row>
            <Row>
                <Tile title={t('uploadPurchaseOrder-tileTitle')} width={'100%'}>
                    <Column>
                        <FileInput
                            type={'multiple'}
                            uploadButtonLabel={t('chooseFiles-buttonLabel')}
                            uploadButtonDisabled={isPageBusy}
                            accept={'.pdf'}
                            onFileAttached={(acceptedFiles) => {
                                Logger.debug(acceptedFiles);
                                addFiles(acceptedFiles);
                            }}
                        />
                        <Text>{t('chooseFiles-buttonHint', { maxSize: MAX_FILE_SIZE_MB })}</Text>
                        {attachedFiles.length ? (
                            <Table headerRows={1}>
                                <TableRow>
                                    <TableCell>{t('uploadedFileName-columnHeader')}</TableCell>
                                    <TableCell>{t('uploadPurchaseOrder-actions')}</TableCell>
                                </TableRow>
                                {attachedFiles.map(({ id, file }) => (
                                    <TableRow key={`${id}_tableRow`}>
                                        <TableCell>
                                            <FileDetails
                                                file={(file as unknown) as FileDetailsFile}
                                                uploadPercentage={getFileProgress(id)}
                                                key={id}
                                                uploadComplete={getFileProgress(id) === 100}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                type={'secondary'}
                                                disabled={isPageBusy}
                                                onClick={() => removeFile(id)}
                                            >
                                                {t('delete-buttonLabel')}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </Table>
                        ) : undefined}
                    </Column>
                </Tile>
            </Row>
            <Row>
                <Button type={'secondary'} onClick={cancel}>
                    {t('cancel-buttonLabel')}
                </Button>
                <Button
                    type={'primary'}
                    isBusy={isPageBusy}
                    disabled={!attachedFiles.length}
                    onClick={() => uploadPurchaseOrders()}
                >
                    {t('submit-buttonLabel')}
                </Button>
            </Row>
            {startedExtractions.length ? (
                <Row>
                    <Tile
                        width={'100%'}
                        title={t('startedExtractions-tableHeading')}
                        contentSpacingInset={'none'}
                        data-testid={'started-extractions-tile'}
                    >
                        <Table headerRows={1}>
                            <TableRow>
                                <TableCell>{t('uploadedFileName-columnHeader')}</TableCell>
                                <TableCell>{t('extractionStatus-columnHeader')}</TableCell>
                                <TableCell>{t('extractionMessage-columnHeader')}</TableCell>
                            </TableRow>
                            {startedExtractions.map((startedExtraction) => (
                                <TableRow key={`${startedExtraction.id}_row`}>
                                    <TableCell>{t.cognate(startedExtraction.fileName)}</TableCell>
                                    <TableCell>
                                        {startedExtraction.errorCode
                                            ? t('extractionStatus', { context: 'FAILED' })
                                            : t('extractionStatus', { context: 'PROCESSING' })}
                                    </TableCell>
                                    <TableCell>
                                        <Text>
                                            {startedExtraction.errorCode
                                                ? t('extractionMessage', { context: startedExtraction.errorCode })
                                                : t('extractionMessage', { context: 'PROCESSING' })}
                                        </Text>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </Table>
                    </Tile>
                </Row>
            ) : undefined}
        </Column>
    );
};

const mapStateToProps = ({ resellerReducer }: RootState) => {
    return {
        resellerId: resellerReducer.selectedReseller.value!.resellerId,
    };
};

const connector = connect(mapStateToProps);
export type UploadPurchaseOrderPageProps = ConnectedProps<typeof connector>;
export default connector(UploadPurchaseOrderPage);
